/**
 * HeatmapPreview Component
 * Renders a heatmap grid using visx — colored rectangles mapped from a sequential palette
 */

import React, { useMemo } from 'react';
import { scaleBand } from '@visx/scale';
import { Group } from '@visx/group';
// Axes rendered manually for precise cell-center alignment
import { useTheme } from '../../contexts/ThemeContext';
import { useData } from '../../contexts/DataContext';
import transformData from '../../utils/transformData';
import { interpolatePalette, getSequentialPalette } from '../../charts/shared/palettes';
import { useChartTooltip } from './useChartTooltip';
import AxisLaneDebugOverlay from './AxisLaneDebugOverlay';
import ChartTitle from './ChartTitle';
import {
  resolveXAxisLabels,
  computeChartLayout,
  computeAxisLanes,
  getAxisSpacingSpec,
  isAxisDebugEnabled,
  reportAxisLaneClamp,
  computeTitleFontSize,
  computeHorizontalLeftMargin,
  formatCompactNumber,
} from '../../utils/chartLayout';

function HeatmapPreview({ chartType, config, width, height }) {
  const { resolved: theme } = useTheme();
  const { isLoaded, derivedData: userData, mapping } = useData();

  // Get data — user data or sample data
  const rawData = useMemo(() => {
    if (isLoaded && userData.length > 0 && Object.keys(mapping).length > 0) {
      return transformData(userData, mapping, chartType.id);
    }
    if (chartType.getSampleData) return chartType.getSampleData();
    return chartType.sampleData;
  }, [isLoaded, userData, mapping, chartType]);

  // Pivot flat rows into matrix + label arrays
  const { xLabels, yLabels, matrix, minVal, maxVal } = useMemo(() => {
    const xSet = new Set();
    const ySet = new Set();
    rawData.forEach(d => {
      if (d.x !== undefined) xSet.add(d.x);
      if (d.y !== undefined) ySet.add(d.y);
    });
    const xLabels = Array.from(xSet);
    const yLabels = Array.from(ySet);

    const lookup = {};
    rawData.forEach(d => { lookup[`${d.y}__${d.x}`] = d.value; });

    const matrix = yLabels.map(y => xLabels.map(x => {
      const v = lookup[`${y}__${x}`];
      return v !== undefined ? v : null;
    }));

    const allVals = rawData.map(d => d.value).filter(v => v !== null && !isNaN(v));
    return {
      xLabels,
      yLabels,
      matrix,
      minVal: allVals.length ? Math.min(...allVals) : 0,
      maxVal: allVals.length ? Math.max(...allVals) : 1,
    };
  }, [rawData]);

  // Color palette
  const palette = useMemo(() => {
    return getSequentialPalette(config, theme);
  }, [config, theme]);

  // Fine-grained color gradient (256 steps)
  const colorGradient = useMemo(() => interpolatePalette(palette, 256), [palette]);

  const getColor = (value) => {
    if (value === null || value === undefined) return '#EEEEEE';
    const t = Math.max(0, Math.min(1, (value - minVal) / (maxVal - minVal || 1)));
    const idx = Math.min(255, Math.floor(t * 256));
    return colorGradient[idx];
  };

  const getTextColor = (value) => {
    const hex = getColor(value);
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return lum > 0.35 ? '#000000' : '#FFFFFF';
  };

  // Margins — shared layout engine
  const { margin, innerWidth, innerHeight } = useMemo(() => computeChartLayout({
    width,
    height,
    title: config.title,
    titleFontSize: computeTitleFontSize(config.title, width) || undefined,
    xLabels: yLabels,
    tickFontSize: theme.tickSize ?? 13,
    tickFontFamily: theme.fontBody,
    hasXLabel: !!config.xlabel,
    hasYLabel: !!config.ylabel,
    orientation: 'horizontal',
    baseRight: 20,
    baseBottom: 55,
    horizontalLeftMax: 140,
    extra: { left: computeHorizontalLeftMargin(yLabels, theme.tickSize ?? 13, 140, theme.fontBody) - 65 },
  }), [width, height, config.title, config.xlabel, config.ylabel, yLabels, theme.tickSize, theme.fontBody]);
  const xAxis = useMemo(() => resolveXAxisLabels({
    labels: xLabels,
    chartWidth: width,
    chartHeight: height,
    baseFontSize: theme.tickSize ?? 13,
    fontFamily: theme.fontBody,
    rotationOverride: config.labelRotation,
    truncateLabels: !!config.truncateXAxisLabels,
  }), [xLabels, width, height, theme.tickSize, theme.fontBody, config.labelRotation, config.truncateXAxisLabels]);
  const { xTickGap, yTickGap } = getAxisSpacingSpec(theme);
  const yLanes = useMemo(() => computeAxisLanes({
    axisSide: 'left',
    labels: yLabels,
    tickFontSize: theme.tickSize ?? 13,
    tickFontFamily: theme.fontBody,
    titleFontSize: theme.labelSize ?? 15,
    showTicks: false,
    tickLength: 0,
    availableMargin: margin.left,
    spacingSpec: { ...getAxisSpacingSpec(theme), gapY: yTickGap },
    telemetryMeta: { chartId: 'heatmap', viewportWidth: width, viewportHeight: height },
  }), [yLabels, margin.left, yTickGap, theme, width, height]);
  const xLanes = useMemo(() => computeAxisLanes({
    axisSide: 'bottom',
    labels: xLabels,
    rotation: xAxis.rotation,
    tickFontSize: xAxis.fontSize,
    tickFontFamily: theme.fontBody,
    titleFontSize: theme.labelSize ?? 15,
    showTicks: false,
    tickLength: 0,
    spacingSpec: { ...getAxisSpacingSpec(theme), gapX: xTickGap },
    telemetryMeta: { chartId: 'heatmap', viewportWidth: width, viewportHeight: height },
  }), [xLabels, xTickGap, theme, width, height, xAxis.rotation, xAxis.fontSize]);
  const axisDebugEnabled = isAxisDebugEnabled();
  if (axisDebugEnabled) {
    reportAxisLaneClamp(yLanes);
    reportAxisLaneClamp(xLanes);
  }

  // Scales — one band per label, no padding (cells abut each other)
  const xScale = useMemo(() => scaleBand({
    domain: xLabels,
    range: [0, innerWidth],
    padding: 0,
  }), [xLabels, innerWidth]);

  const yScale = useMemo(() => scaleBand({
    domain: yLabels,
    range: [0, innerHeight],
    padding: 0,
  }), [yLabels, innerHeight]);

  const cellWidth = xScale.bandwidth();
  const cellHeight = yScale.bandwidth();

  const decimals = config.valueDecimals ?? 1;

  // ─── Tooltip ───────────────────────────────────────────────────────────────
  const { containerRef, showTip, hideTooltip, Tip } = useChartTooltip(theme);

  const axisLabelProps = {
    fill: theme.labelColor || theme.foreground || '#000',
    fontSize: theme.labelSize ?? 15,
    fontFamily: theme.fontBody || theme.fontHeading || 'Cohere Text',
  };

  return (
    <div ref={containerRef} style={{ width, height, position: 'relative', backgroundColor: theme.background }}>
      <ChartTitle title={config.title} chartWidth={width} marginLeft={margin.left} marginRight={margin.right} theme={theme} />

      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%" }} overflow="visible" onMouseLeave={hideTooltip}>
        <Group left={margin.left} top={margin.top}>
          {/* Heatmap cells */}
          {yLabels.map((yLabel, yi) =>
            xLabels.map((xLabel, xi) => {
              const value = matrix[yi]?.[xi];
              const cellColor = getColor(value);
              const cx = xScale(xLabel) || 0;
              const cy = yScale(yLabel) || 0;

              return (
                <g key={`cell-${yi}-${xi}`}>
                  <rect
                    x={cx}
                    y={cy}
                    width={cellWidth}
                    height={cellHeight}
                    fill={cellColor}
                    stroke={config.showCellBorder ? '#FFFFFF' : 'none'}
                    strokeWidth={config.showCellBorder ? 1 : 0}
                    style={{ cursor: 'pointer' }}
                    onMouseMove={(e) => showTip(e, formatCompactNumber(value, decimals))}
                  />
                  {config.showValues && value !== null && (
                    <text
                      x={cx + cellWidth / 2}
                      y={cy + cellHeight / 2}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize={Math.min(theme.valueLabelSize ?? 12, cellHeight * 0.35)}
                      fill={getTextColor(value)}
                      fontFamily={theme.fontBody || 'Cohere Text'}
                    >
                      {formatCompactNumber(value, decimals)}
                    </text>
                  )}
                </g>
              );
            })
          )}

          {/* Y axis spine */}
          <line
            x1={0}
            y1={0}
            x2={0}
            y2={innerHeight}
            stroke={theme.axisColor || '#000'}
            strokeWidth={theme.spineWidth ?? 1}
          />

          {/* Y axis — category labels on the left, centered on each row */}
          <g>
            {yLabels.map((label, yi) => {
              const cy = (yScale(label) || 0) + cellHeight / 2;
              return (
                <text
                  key={`ylabel-${yi}`}
                  x={-yTickGap}
                  y={cy}
                  textAnchor="end"
                  dominantBaseline="central"
                  fontSize={theme.tickSize ?? 13}
                  fill={theme.tickColor || theme.foreground || '#000'}
                  fontFamily={theme.fontBody || 'Cohere Text'}
                >
                  {label}
                </text>
              );
            })}
          </g>

          {/* X axis — category labels at bottom */}
          <g>
            {xAxis.tickValues.map((label, xi) => {
              const cx = (xScale(label) || 0) + cellWidth / 2;
              const cy = innerHeight + xTickGap;
              return (
                <text
                  key={`xlabel-${xi}`}
                  x={cx}
                  y={cy}
                  dominantBaseline={xAxis.tickLabelProps.dominantBaseline || (xAxis.rotation ? 'auto' : 'hanging')}
                  textAnchor={xAxis.tickLabelProps.textAnchor || 'middle'}
                  fontSize={xAxis.fontSize}
                  fill={theme.tickColor || theme.foreground || '#000'}
                  fontFamily={theme.fontBody || 'Cohere Text'}
                  transform={xAxis.rotation ? `rotate(-${xAxis.rotation}, ${cx}, ${cy})` : undefined}
                >
                  {xAxis.tickFormat(label)}
                </text>
              );
            })}
          </g>

          {/* Axis labels */}
          {config.xlabel && (
            <text
              x={innerWidth / 2}
              y={innerHeight + xLanes.titleOffsetPx}
              textAnchor="middle"
              {...axisLabelProps}
            >
              {config.xlabel}
            </text>
          )}
          {config.ylabel && (
            <text
              x={-innerHeight / 2}
              y={-yLanes.titleOffsetPx}
              transform="rotate(-90)"
              textAnchor="middle"
              {...axisLabelProps}
            >
              {config.ylabel}
            </text>
          )}
          {axisDebugEnabled && (
            <AxisLaneDebugOverlay
              lanes={[
                {
                  axisSide: 'left',
                  tickLabelOffsetPx: yLanes.tickLabelOffsetPx,
                  titleOffsetPx: yLanes.titleOffsetPx,
                  clamped: yLanes.clamped,
                  spineX: 0,
                  spineY1: 0,
                  spineY2: innerHeight,
                },
                {
                  axisSide: 'bottom',
                  tickLabelOffsetPx: xLanes.tickLabelOffsetPx,
                  titleOffsetPx: xLanes.titleOffsetPx,
                  clamped: xLanes.clamped,
                  spineY: innerHeight,
                  spineX1: 0,
                  spineX2: innerWidth,
                },
              ]}
            />
          )}
        </Group>
      </svg>
      <Tip />
    </div>
  );
}

export default HeatmapPreview;
